import { useState, useEffect } from "react";
import { useAuth } from "@/lib/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import { UserCircle, Key, Shield, Eye, EyeOff, Clock, Check, AlertCircle, Fingerprint } from "lucide-react";
import { toast } from "sonner";
import { BackgroundPicker } from "@/components/BackgroundPicker";
import { startTour } from "@/components/GuidedTour";
import { Compass } from "lucide-react";
import axios from "axios";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function ProfilePage() {
  const { user, logout, autoLogoutMin, updateAutoLogoutMin } = useAuth();
  const [displayName, setDisplayName] = useState("");
  const [saving, setSaving] = useState(false);
  const [biometricEnabled, setBiometricEnabled] = useState(false);

  useEffect(() => {
    // Check biometric status
    try {
      const data = JSON.parse(localStorage.getItem("rj_biometric") || "{}");
      const email = user?.email?.toLowerCase();
      if (email && data[email]?.credentialId) {
        setBiometricEnabled(true);
      }
    } catch {}
  }, [user]);

  // Password
  const [currentPw, setCurrentPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [pwSaving, setPwSaving] = useState(false);

  useEffect(() => {
    if (user) setDisplayName(user.name || "");
  }, [user]);

  const handleSaveName = async () => {
    if (!displayName.trim()) { toast.error("Name cannot be empty"); return; }
    setSaving(true);
    try {
      await axios.put(`${BACKEND}/api/auth/profile`, { name: displayName.trim() }, { withCredentials: true });
      toast.success("Display name updated");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update");
    }
    setSaving(false);
  };

  const handleChangePassword = async () => {
    if (!currentPw) { toast.error("Enter current password"); return; }
    if (newPw.length < 4) { toast.error("New password must be at least 4 characters"); return; }
    if (newPw !== confirmPw) { toast.error("Passwords don't match"); return; }
    setPwSaving(true);
    try {
      await axios.put(`${BACKEND}/api/auth/password`, { current_password: currentPw, new_password: newPw }, { withCredentials: true });
      toast.success("Password changed successfully");
      setCurrentPw(""); setNewPw(""); setConfirmPw("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to change password");
    }
    setPwSaving(false);
  };

  if (!user) return null;

  return (
    <div className="max-w-[600px] mx-auto px-3 sm:px-4 md:px-8 py-4 sm:py-6" data-testid="profile-page">
      <button onClick={startTour} data-testid="profile-replay-tour"
        className="w-full mb-6 flex items-center gap-3 px-5 py-4 bg-bt-surface border border-bt-border-subtle hover:border-bt-gold/60 transition-colors text-left">
        <Compass className="w-5 h-5 text-bt-gold shrink-0" strokeWidth={1.5} />
        <span className="flex-1">
          <span className="block text-sm font-medium text-bt-text-primary">Take the guided tour</span>
          <span className="block text-xs text-bt-text-tertiary">A quick walkthrough of Markets, Woodrow, the Leaderboard and more</span>
        </span>
        <span className="text-xs font-mono text-bt-gold">Start →</span>
      </button>
      {/* Profile Settings */}
      <div className="bg-bt-surface border border-bt-border-subtle p-6 mb-6">
        <div className="flex items-center gap-3 mb-1">
          <UserCircle className="w-5 h-5 text-bt-gold" strokeWidth={1.5} />
          <h2 className="font-heading text-xl font-medium text-bt-text-primary">Profile Settings</h2>
        </div>
        <p className="text-sm text-bt-text-secondary font-body mb-6">Update your display name and password</p>

        {/* Email */}
        <div className="mb-5">
          <Label className="text-bt-text-primary font-body text-sm font-medium">Email</Label>
          <Input
            value={user.email}
            disabled
            className="mt-1 bg-bt-surface-2 border-bt-border-subtle text-bt-text-tertiary font-mono"
            data-testid="profile-email"
          />
        </div>

        {/* Display Name */}
        <div className="mb-2">
          <Label className="text-bt-text-primary font-body text-sm font-medium">Display Name</Label>
          <Input
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder="Your display name"
            className="mt-1 bg-bt-surface-2 border-bt-border-subtle text-bt-text-primary font-mono"
            data-testid="profile-name-input"
          />
        </div>
        <Button
          onClick={handleSaveName}
          disabled={saving}
          size="sm"
          className="bg-bt-gold text-bt-bg hover:bg-bt-gold-light font-body text-xs mb-2"
          data-testid="save-name-btn"
        >
          <Check className="w-3 h-3 mr-1" /> {saving ? "Saving..." : "Save Name"}
        </Button>
      </div>

      {/* Change Password */}
      <div className="bg-bt-surface border border-bt-border-subtle p-6 mb-6">
        <div className="flex items-center gap-3 mb-1">
          <Key className="w-5 h-5 text-bt-gold" strokeWidth={1.5} />
          <h2 className="font-heading text-xl font-medium text-bt-text-primary">Change PIN/Password</h2>
        </div>
        <Separator className="bg-bt-border-subtle my-4" />

        <div className="space-y-4">
          <div>
            <Label className="text-bt-text-primary font-body text-sm font-medium">Current PIN/Password</Label>
            <div className="relative mt-1">
              <Input
                type={showCurrent ? "text" : "password"}
                value={currentPw}
                onChange={(e) => setCurrentPw(e.target.value)}
                placeholder="Enter current PIN/password"
                className="pr-10 bg-bt-surface-2 border-bt-border-subtle text-bt-text-primary font-mono"
                data-testid="current-pw-input"
              />
              <button
                type="button"
                onClick={() => setShowCurrent(!showCurrent)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-bt-text-tertiary hover:text-bt-text-primary"
                data-testid="toggle-current-pw"
              >
                {showCurrent ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
          <div>
            <Label className="text-bt-text-primary font-body text-sm font-medium">New PIN/Password</Label>
            <div className="relative mt-1">
              <Input
                type={showNew ? "text" : "password"}
                value={newPw}
                onChange={(e) => setNewPw(e.target.value)}
                placeholder="Enter new PIN/password (min 4 characters)"
                className="pr-10 bg-bt-surface-2 border-bt-border-subtle text-bt-text-primary font-mono"
                data-testid="new-pw-input"
              />
              <button
                type="button"
                onClick={() => setShowNew(!showNew)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-bt-text-tertiary hover:text-bt-text-primary"
                data-testid="toggle-new-pw"
              >
                {showNew ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
          <div>
            <Label className="text-bt-text-primary font-body text-sm font-medium">Confirm New PIN/Password</Label>
            <Input
              type="password"
              value={confirmPw}
              onChange={(e) => setConfirmPw(e.target.value)}
              placeholder="Confirm new PIN/password"
              className="mt-1 bg-bt-surface-2 border-bt-border-subtle text-bt-text-primary font-mono"
              data-testid="confirm-pw-input"
            />
            {confirmPw && newPw && confirmPw !== newPw && (
              <p className="text-xs text-bt-negative mt-1 flex items-center gap-1"><AlertCircle className="w-3 h-3" /> Passwords don't match</p>
            )}
          </div>
          <Button
            onClick={handleChangePassword}
            disabled={pwSaving}
            size="sm"
            className="bg-bt-gold text-bt-bg hover:bg-bt-gold-light font-body text-xs"
            data-testid="change-pw-btn"
          >
            <Key className="w-3 h-3 mr-1" /> {pwSaving ? "Changing..." : "Change Password"}
          </Button>
        </div>
      </div>

      {/* Security */}
      <div className="bg-bt-surface border border-bt-border-subtle p-6">
        <div className="flex items-center gap-3 mb-1">
          <Shield className="w-5 h-5 text-bt-gold" strokeWidth={1.5} />
          <h2 className="font-heading text-xl font-medium text-bt-text-primary">Security</h2>
        </div>
        <Separator className="bg-bt-border-subtle my-4" />

        <div className="bg-bt-surface-2 border border-bt-border-subtle p-4 flex items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <Clock className="w-5 h-5 text-bt-text-secondary mt-0.5" strokeWidth={1.5} />
            <div>
              <p className="font-body text-sm font-medium text-bt-text-primary">Auto-Logout Timer</p>
              <p className="text-xs text-bt-text-secondary mt-0.5">
                Automatically log out after this many minutes of inactivity to protect your portfolio.
              </p>
              <p className="text-xs text-bt-gold mt-1 flex items-center gap-1">
                <Shield className="w-3 h-3" /> Warning shows 1 minute before auto-logout
              </p>
            </div>
          </div>
          <Select value={String(autoLogoutMin)} onValueChange={(v) => updateAutoLogoutMin(parseInt(v))}>
            <SelectTrigger className="w-[100px] bg-bt-surface border-bt-border-subtle text-bt-text-primary font-mono text-sm" data-testid="auto-logout-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-bt-surface border-bt-border-subtle">
              <SelectItem value="5" className="font-mono text-sm text-bt-text-primary">5 min</SelectItem>
              <SelectItem value="10" className="font-mono text-sm text-bt-text-primary">10 min</SelectItem>
              <SelectItem value="15" className="font-mono text-sm text-bt-text-primary">15 min</SelectItem>
              <SelectItem value="25" className="font-mono text-sm text-bt-text-primary">25 min</SelectItem>
              <SelectItem value="60" className="font-mono text-sm text-bt-text-primary">60 min</SelectItem>
              <SelectItem value="120" className="font-mono text-sm text-bt-text-primary">2 hrs</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Biometric Login */}
        <div className="bg-bt-surface-2 border border-bt-border-subtle p-4 flex items-start justify-between gap-4" data-testid="biometric-settings">
          <div className="flex items-start gap-3">
            <Fingerprint className="w-5 h-5 text-bt-text-secondary mt-0.5" strokeWidth={1.5} />
            <div>
              <p className="font-body text-sm font-medium text-bt-text-primary">Fingerprint / Face ID</p>
              <p className="text-xs text-bt-text-secondary mt-0.5">
                {biometricEnabled
                  ? "Biometric login is enabled on this device. You can sign in with your fingerprint or face."
                  : "Not set up. Log in on a mobile device to enable biometric authentication."
                }
              </p>
            </div>
          </div>
          {biometricEnabled ? (
            <div className="flex flex-col items-end gap-1.5 shrink-0">
              <span className="text-[10px] font-mono text-bt-positive flex items-center gap-1">
                <Check className="w-3 h-3" /> Enabled
              </span>
              <Button
                variant="outline"
                size="sm"
                className="border-bt-negative/30 text-bt-negative hover:bg-bt-negative/10 text-xs font-body"
                onClick={() => {
                  try {
                    const data = JSON.parse(localStorage.getItem("rj_biometric") || "{}");
                    delete data[user?.email?.toLowerCase()];
                    localStorage.setItem("rj_biometric", JSON.stringify(data));
                    if (localStorage.getItem("rj_biometric_email") === user?.email?.toLowerCase()) {
                      localStorage.removeItem("rj_biometric_email");
                    }
                  } catch {}
                  setBiometricEnabled(false);
                  toast.success("Biometric login has been reset. You can set it up again on your next login.");
                }}
                data-testid="reset-biometric-btn"
              >
                Reset
              </Button>
            </div>
          ) : (
            <span className="text-[10px] font-mono text-bt-text-tertiary shrink-0">Not Set Up</span>
          )}
        </div>
      </div>
      <BackgroundPicker />
    </div>
  );
}
